import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstant from "../../api";
import {
    createProposalEndpoint,
    publicProposalDetailEndpoint,
    publicProposalEmailEndpoint,
    rmnProposalConfigEndpoint,
    rmnProposalEndpoint,
    rmnProposalPreviewEndpoint,
    rmnProposalsEndpoint,
} from "../../api/endpoints";
import { TurnstileWidget } from "../PdpAudit/TurnstileWidget";
import { useUser } from "../../hooks/useUser";
import { buildRegisterHandoffPath } from "../../utils/funnelHandoff";
import { hideAllPricing, isInternal } from "../../utils";
import { Step1Objective } from "./Step1Objective";
import { Step2Channels } from "./Step2Channels";
import { Step3Packages } from "./Step3Packages";
import { WizardStepper } from "./WizardStepper";
import { downloadProposalPdf, MAX_EMAIL_PDF_BASE64_CHARS, proposalPdfBase64 } from "./pdf/downloadProposalPdf";
import { apiErrorCode } from "../../utils/apiError";
import { computeUnits, strategyFromWizard } from "./strategy/StrategyData";
import { useProposalExamples } from "./useProposalExamples";
import { useProposalPricingPreview } from "./useProposalPricingPreview";
import { useProposalSuggestions } from "./useProposalSuggestions";
import { useProposalWizardConfig } from "./useProposalWizardConfig";
import { BLACK, BORDER, CARD_BG, DISABLED_BG, FONT, GOLD_DARK, GOLD_TINT, GREEN, GREEN_BG, INK_BG, INK_TEXT, RED, RED_BG, TEXT_FAINT, TEXT_MUTED } from "./theme";
import {
    CreatedProposal,
    PdpSubtype,
    ProposalDetail,
    ProposalProductInput,
    ProposalProductLinkInput,
    ProposalTier,
    SelectedChannel,
    WizardProductRef,
} from "./types";

// Reusable 3-step "Choose Your Path" proposal wizard:
//   1. product category + primary objective (+ optional secondary) →
//   2. channels (qty per channel) →
//   3. good/better/best package with the full slide-3 strategy breakdown.
//
// The strategy is saved the moment step 3 is revealed: authenticated viewers
// on arrival, anonymous leads when they pass the email + captcha gate (which
// also captures the lead). From then on every change is an autosaved edit
// (PATCH), and step 3 carries the share actions (download, email, link,
// create account) — there is no separate submit or "finished" screen.
//
// Works both as the bare public page (/proposal, pdp-audit style) and inside
// a Dialog for internal users (ProposalFlowModal). The server never creates
// brands/products from anonymous input (product info rides along as proposal
// metadata).
//
// Edit mode (`edit` prop): prefills every step from an existing proposal,
// starts on step 3, and saves explicitly to the given endpoint (Cancel must
// still discard) — used by the internal detail page, the public lead view
// and the network strategies list.
export interface ObjectiveProposalFlowProps {
    product?: ProposalProductInput;
    brands?: Array<{ id: number; name?: string }>;
    defaultCategoryId?: number;
    email?: string;
    requireCaptcha?: boolean;
    // Links the created proposal back to the PDP audit it came from (the
    // audit report CTA and the sales-admin button both pass this).
    pdpAuditReportId?: number | null;
    // Or a catalog product's latest PDP score — the Products page's Create
    // Strategy — as the suggestion source (no audit report row exists).
    pdpUrlScoreId?: number | null;
    // Or a Consumer Voice analysis (product or brand) — the report view's
    // Create strategy CTA; anonymous rows also carry their access token.
    consumerVoiceAuditId?: number | null;
    // Or an AI Presence analysis (product or brand) — same CTA pattern.
    aiPresenceAuditId?: number | null;
    // Anonymous Consumer Voice / AI Presence rows carry their access token.
    intelAccessToken?: string | null;
    // Pre-seeded product links for the Step-1 picker (TDC-1499): the
    // Products-page quick action and the audit CTAs pass ?desireid= / ?dp=.
    initialProducts?: WizardProductRef[];
    // Network lens: config/preview/create hit the rmnControl-scoped
    // /rmn/proposals/* endpoints (filtered content types + placements for
    // RMN roots; unrestricted for other brands) and payloads carry brand_id
    // when a supplier brand is chosen. Captcha is never shown (the portal is
    // behind auth). Slug is only set on the legacy white-label route —
    // pass {} for the slug-free portal wizard (TDC-1499).
    rmn?: { brandId?: number; slug?: string };
    // PDP Autopilot (portal wizard opened from a recommendation's "Add to
    // strategy"): the objective to start on, and the recommendation ids the
    // create call links to the new strategy. Absent = the wizard is unchanged.
    initialObjectiveId?: number | null;
    recommendationIds?: number[];
    onCreated?: (proposal: CreatedProposal) => void;
    edit?: { proposal: ProposalDetail; endpoint: string };
    onSaved?: (proposal: ProposalDetail) => void;
    onCancelEdit?: () => void;
}

type Step = 1 | 2 | 3;

const buttonBase: React.CSSProperties = {
    fontFamily: FONT,
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 10,
    padding: "12px 22px",
    cursor: "pointer",
    border: "none",
};

// Mirrors the server's EMAIL_RE (proposals.js) — gates the step-3 reveal.
const EMAIL_OK_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const expandChannels = (detail: ProposalDetail): SelectedChannel[] => {
    const out: SelectedChannel[] = [];
    detail.channels.forEach((ch) => {
        for (let i = 0; i < ch.quantity; i += 1) out.push({ id: ch.id, name: ch.name });
    });
    return out;
};

export function ObjectiveProposalFlow({
    product,
    brands,
    defaultCategoryId,
    email: emailProp,
    requireCaptcha = false,
    pdpAuditReportId,
    pdpUrlScoreId,
    consumerVoiceAuditId,
    aiPresenceAuditId,
    intelAccessToken,
    initialProducts,
    rmn,
    initialObjectiveId,
    recommendationIds,
    onCreated,
    edit,
    onSaved,
    onCancelEdit,
}: ObjectiveProposalFlowProps) {
    const { config, loading, error: configError } = useProposalWizardConfig(
        rmn ? rmnProposalConfigEndpoint : undefined,
    );
    const { user, role } = useUser();
    const internalViewer = !!user && isInternal(role);
    // Client-facing terminology: the brand portal (rmn prop set) and the
    // public wizard/lead pages say "strategy"; only the internal sales tools
    // (ProposalFlowModal, internal edit mode) keep "proposal".
    const clientCopy = rmn || !internalViewer;
    const noun = clientCopy ? "strategy" : "proposal";
    const nounPlural = clientCopy ? "strategies" : "proposals";
    // Real pricing renders only for authenticated sessions (internal, brand);
    // anonymous wizard users get neutral tier cards + the teaser line. The
    // API already nulls anonymous preview totals — this flag picks the copy
    // (TDC-1563).
    const authedViewer = !!user;
    // TDC-1591: RMN work shows no pricing at all — to anyone, internal staff
    // included. Since TDC-1499 opened the /rmn/* wizard to every brand, the
    // `rmn` prop no longer implies an RMN brand — only a config the server
    // lensed (rmn:true roots) does; internal edit mode (non-lensed config)
    // reads the proposal's own brand instead. RMN users on the public wizard
    // are caught by the viewer fallback.
    let pricingEntity: unknown = null;
    if (config && config.rmn) pricingEntity = { brand: { rmn: true } };
    else if (edit) pricingEntity = edit.proposal;
    const pricingHidden = hideAllPricing(role, user, pricingEntity);
    const editing = !!edit;

    const [step, setStep] = useState<Step>(editing ? 3 : 1);
    const [categoryId, setCategoryId] = useState<number | null>(
        editing && edit!.proposal.product_category
            ? edit!.proposal.product_category.id
            : defaultCategoryId || null,
    );
    // RMN wizard: the retailer-category pick. Its crawl-time Gemini mapping
    // pre-selects the TDC category below and the shopper can still change
    // either; it is ALSO submitted (proposal_meta stamp) so the strategies
    // list can filter product-less strategies by retailer category.
    const [rmnCategoryId, setRmnCategoryId] = useState<number | null>(null);
    // Linked products (TDC-1499) — imported desires and/or discovered
    // staging rows; the server resolves everything to desires at create.
    // Edit mode shows the saved links read-only (link editing is deferred).
    const [selectedProducts, setSelectedProducts] = useState<WizardProductRef[]>(
        editing && edit!.proposal.products
            ? edit!.proposal.products.map((p) => ({
                kind: "desire" as const,
                desireid: p.desireid,
                title: p.title,
                image: p.image || undefined,
            }))
            : initialProducts || [],
    );
    const [objectiveId, setObjectiveId] = useState<number | null>(
        editing && edit!.proposal.objective ? edit!.proposal.objective.id : null,
    );
    const [secondaryObjectiveId, setSecondaryObjectiveId] = useState<number | null>(
        editing && edit!.proposal.secondary_objective
            ? edit!.proposal.secondary_objective.id
            : null,
    );
    const [selectedChannels, setSelectedChannels] = useState<SelectedChannel[]>(
        editing ? expandChannels(edit!.proposal) : [],
    );
    const [tier, setTier] = useState<ProposalTier | null>(
        editing ? edit!.proposal.tier : null,
    );
    // PDP sub-type (Retail | D2C | Amazon) — a refinement of the PDP channel,
    // only submitted when the PDP channel is actually selected.
    const [pdpSubtype, setPdpSubtype] = useState<PdpSubtype>(
        (editing && edit!.proposal.pdp_subtype) || "retail",
    );
    const [email, setEmail] = useState(emailProp || "");
    const [copiedLink, setCopiedLink] = useState(false);
    // The captcha is only needed for the anonymous create at the gate —
    // autosaved edits ride the share token, so no token-freshness tracking.
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    // Bumped when the server rejects a token: keys the gate widget so it
    // remounts and issues a fresh token (the widget has no reset API, and a
    // consumed token looks fresh to it until expiry).
    const [captchaNonce, setCaptchaNonce] = useState(0);
    const [created, setCreated] = useState<CreatedProposal | null>(null);
    // Autosave status for the step-3 bar ("idle" until the first save).
    const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
    const [saveError, setSaveError] = useState<string | null>(null);
    // Latest server pricing total — create response, then each autosave.
    const [pricingTotal, setPricingTotal] = useState<string | number | null>(null);
    // Edit mode's explicit Save.
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [emailingPdf, setEmailingPdf] = useState(false);
    const [emailedPdf, setEmailedPdf] = useState(false);
    const [emailPdfError, setEmailPdfError] = useState<string | null>(null);

    const objective = useMemo(
        () => (config ? config.objectives.find((o) => o.id === objectiveId) : undefined),
        [config, objectiveId],
    );
    const secondaryObjective = useMemo(
        () =>
            config
                ? config.objectives.find((o) => o.id === secondaryObjectiveId)
                : undefined,
        [config, secondaryObjectiveId],
    );
    const categoryName = useMemo(() => {
        if (!config || !categoryId) return null;
        const cat = config.product_categories.find((c) => c.id === categoryId);
        return cat ? cat.name : null;
    }, [config, categoryId]);

    const handleRmnCategoryChange = (id: number | null) => {
        setRmnCategoryId(id);
        if (id == null || !config || !config.rmn_categories) return;
        const cat = config.rmn_categories.find((c) => c.id === id);
        if (cat && cat.product_category_id != null) {
            setCategoryId(cat.product_category_id);
        }
    };

    // Entering step 2 (or switching objective) seeds the selection with the
    // objective's suggested channels, one placement each. In edit mode the
    // proposal's saved channels are the seed — skip the first run only, so
    // switching objective mid-edit still reseeds.
    const seededRef = useRef(editing);
    useEffect(() => {
        if (seededRef.current) {
            seededRef.current = false;
            return;
        }
        if (!objective) {
            setSelectedChannels([]);
            return;
        }
        setSelectedChannels(objective.channels.map((c) => ({ id: c.id, name: c.name })));
        setTier(null);
    }, [objectiveId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Primary switched onto the current secondary — drop the duplicate.
    useEffect(() => {
        if (objectiveId != null && objectiveId === secondaryObjectiveId) {
            setSecondaryObjectiveId(null);
        }
    }, [objectiveId, secondaryObjectiveId]);

    // Entered from an audit (report or product score): ask Gemini to pre-fill
    // category + objectives from the audit JSON. Applied once, and only
    // while the user hasn't made their own picks — a slow response never
    // clobbers a manual selection. A category pre-seeded from the URL is
    // NOT a user pick: the suggestion is the better-informed choice and
    // overrides it (that's the point of the picker).
    const { suggestion, loading: suggestionLoading } = useProposalSuggestions(
        {
            pdpAuditReportId,
            pdpUrlScoreId,
            consumerVoiceAuditId,
            aiPresenceAuditId,
            accessToken: intelAccessToken,
            url: product && product.url ? product.url : null,
        },
        (emailProp || email).trim(),
        !editing && !created,
    );
    // Banner copy for whichever analysis the suggestion is drawn from.
    let suggestionSource = { name: "PDP audit", basis: "your audit results" };
    if (consumerVoiceAuditId) suggestionSource = { name: "Consumer Voice analysis", basis: "what shoppers say" };
    else if (aiPresenceAuditId) suggestionSource = { name: "AI Presence analysis", basis: "how AI answers shoppers" };
    const userPickedRef = useRef(false);
    const [suggestionApplied, setSuggestionApplied] = useState(false);
    const suggestionAppliedRef = useRef(false);
    useEffect(() => {
        if (!suggestion || suggestionAppliedRef.current) return;
        if (userPickedRef.current) return;
        suggestionAppliedRef.current = true;
        if (suggestion.product_category_id != null) {
            setCategoryId(suggestion.product_category_id);
        }
        if (suggestion.objective_id != null) {
            setObjectiveId(suggestion.objective_id);
        }
        if (suggestion.secondary_objective_id != null) {
            setSecondaryObjectiveId(suggestion.secondary_objective_id);
        }
        setSuggestionApplied(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestion]);

    // Autopilot prefill: the objective arrives after the page resolves the
    // recommendation, so apply it once, and only while the user has not
    // picked one themselves.
    useEffect(() => {
        if (editing || created) return;
        if (initialObjectiveId == null || userPickedRef.current) return;
        setObjectiveId((cur) => (cur == null ? initialObjectiveId : cur));
    }, [initialObjectiveId]);

    const { preview, loading: previewLoading } = useProposalPricingPreview(
        objectiveId,
        secondaryObjectiveId,
        selectedChannels,
        step >= 2,
        rmn ? { endpoint: rmnProposalPreviewEndpoint, brandId: rmn.brandId } : undefined,
    );

    // Deck default: pre-select the recommended middle package on step 3.
    useEffect(() => {
        if (step === 3 && !tier) setTier("better");
    }, [step, tier]);

    // Demo videos + top experts for the selected category, scoped to the
    // mix's content types — feeds the "See it in action" strip + the PDF.
    const exampleContentTypeIds = useMemo(
        () =>
            computeUnits(objective, secondaryObjective, tier || "better").map(
                (u) => u.contentTypeId,
            ),
        [objective, secondaryObjective, tier],
    );
    const examples = useProposalExamples(
        categoryId,
        exampleContentTypeIds,
        step === 3,
    );

    const needsEmail = !user && !editing;
    // Arriving from a PDP audit's "Create Proposal" CTA carries the email the
    // lead already gave for the audit (?email=&audit=) — don't recollect it:
    // the gate arrives prefilled and saves on its own once the captcha
    // solves, so the proposal still links to both the lead and the audit
    // (the server's claim rule verifies the email matches the audit's lead).
    // A missing or mangled handoff email falls back to the plain email gate.
    const emailKnown =
        !!pdpAuditReportId && EMAIL_OK_RE.test((emailProp || "").trim());
    // Step-3 email gate: anonymous users give their email (+ captcha) to
    // reveal the good/better/best packages — and that reveal IS the save.
    const emailGateActive = needsEmail && !created;
    const canContinueStep1 = !!categoryId && !!objectiveId;
    const canContinueStep2 = selectedChannels.length > 0;
    const captchaRequired = requireCaptcha && !rmn;
    const canSubmit = !!tier && !submitting;
    // The gate overlay collects BOTH the email and the captcha, so the
    // reveal unlocks only when both are satisfied.
    const gateReady =
        EMAIL_OK_RE.test(email.trim()) &&
        (!captchaRequired || !!captchaToken) &&
        saveState !== "saving";

    // The sub-type only means something while PDP is in the mix — null
    // clears a previously saved value when PDP is deselected.
    const hasPdp = selectedChannels.some((c) => c.name === "PDP");
    const editBody = {
        objective_id: objectiveId,
        secondary_objective_id: secondaryObjectiveId,
        product_category_id: categoryId,
        tier,
        channel_ids: selectedChannels.map((c) => c.id),
        pdp_subtype: hasPdp ? pdpSubtype : null,
    };
    const editSig = JSON.stringify(editBody);
    // Saves only happen from step 3 with a complete selection: a change made
    // back on steps 1–2 (e.g. a new objective, which clears the tier) is
    // persisted when the viewer returns to the strategy.
    const strategyReady =
        step === 3 && !!tier && !!objectiveId && !!categoryId && selectedChannels.length > 0;

    // Autosave plumbing lives in refs so an in-flight save always reads the
    // newest selection and never overlaps another: each PATCH rebuilds the
    // proposal's projects, and two concurrent rebuilds would interleave.
    const latestRef = useRef({ sig: editSig, body: editBody, ready: strategyReady });
    latestRef.current = { sig: editSig, body: editBody, ready: strategyReady };
    const createdRef = useRef<CreatedProposal | null>(null);
    const savedSigRef = useRef<string | null>(null);
    const savingRef = useRef(false);
    // Set once the server reports the proposal confirmed — stop autosaving.
    const lockedRef = useRef(false);
    const autoCreateTriedRef = useRef(false);

    const updateEndpoint = (c: CreatedProposal) =>
        rmn ? rmnProposalEndpoint(c.id) : publicProposalDetailEndpoint(c.share_token || "");

    const saveErrorMessage = (err: any, creating: boolean) => {
        const body = err && err.response ? err.response.data : null;
        const code = body ? body.error : null;
        if (code === "CaptchaFailed") return "The captcha didn't verify. Please tick the checkbox and try again.";
        if (code === "rate_limit_exceeded") {
            return body.message || `Too many ${nounPlural} from this network. Please try again later.`;
        }
        if (code === "EmailRequired") return "Please enter your email address so our team can follow up.";
        if (code === "ProposalNotEditable") return `This ${noun} has been confirmed and can no longer be edited.`;
        return creating
            ? `Something went wrong saving your ${noun}. Please try again.`
            : "We couldn't save your latest change.";
    };

    // Drains pending edits one PATCH at a time until the server matches the
    // on-screen selection.
    const flushEdits = async () => {
        if (savingRef.current || lockedRef.current) return;
        savingRef.current = true;
        try {
            for (;;) {
                const { current } = createdRef;
                const latest = latestRef.current;
                if (!current || !latest.ready || latest.sig === savedSigRef.current) break;
                setSaveState("saving");
                setSaveError(null);
                // Sequential on purpose: one project rebuild at a time.
                // eslint-disable-next-line no-await-in-loop
                const { data } = await axiosInstant.patch<ProposalDetail>(updateEndpoint(current), latest.body);
                savedSigRef.current = latest.sig;
                setPricingTotal(data && data.pricing ? data.pricing.total : null);
            }
            setSaveState("saved");
        } catch (err: any) {
            if (apiErrorCode(err) === "ProposalNotEditable") lockedRef.current = true;
            setSaveState("error");
            setSaveError(saveErrorMessage(err, false));
        } finally {
            savingRef.current = false;
        }
    };

    // First save: POST the proposal (and, for anonymous viewers, the lead).
    const createStrategy = async () => {
        if (createdRef.current || savingRef.current) return;
        const latest = latestRef.current;
        if (!latest.ready || !objectiveId || !categoryId || !tier) return;
        savingRef.current = true;
        setSaveState("saving");
        setSaveError(null);
        try {
            const { data } = await axiosInstant.post<CreatedProposal>(
                rmn ? rmnProposalsEndpoint : createProposalEndpoint,
                {
                    objective_id: objectiveId,
                    secondary_objective_id: secondaryObjectiveId || undefined,
                    product_category_id: categoryId,
                    tier,
                    channel_ids: selectedChannels.map((c) => c.id),
                    pdp_subtype: hasPdp ? pdpSubtype : undefined,
                    product: product && (product.desireid || product.title) ? product : undefined,
                    // Linked products (TDC-1499): discovered picks are
                    // auto-imported server-side; the network wizard only.
                    products:
                        rmn && selectedProducts.length
                            ? selectedProducts.map(
                                (p): ProposalProductLinkInput =>
                                    p.kind === "discovered"
                                        ? { discovered_product_id: Number(p.id) }
                                        : { desireid: Number(p.desireid) },
                            )
                            : undefined,
                    rmn_category_id: (rmn && rmnCategoryId) || undefined,
                    brand_id:
                        (rmn && rmn.brandId) ||
                        (brands && brands.length > 0 ? brands[0].id : undefined),
                    email: email.trim() || undefined,
                    captchaToken: captchaToken || undefined,
                    pdp_audit_report_id: pdpAuditReportId || undefined,
                    // The analysis suggestion's strategy description.
                    description: (suggestion && suggestion.description) || undefined,
                    // PDP Autopilot lineage (attachRecommendationsToOrder).
                    recommendation_ids:
                        recommendationIds && recommendationIds.length ? recommendationIds : undefined,
                },
            );
            createdRef.current = data;
            savedSigRef.current = latest.sig;
            setCreated(data);
            setPricingTotal(data.pricing_total);
            setSaveState("saved");
            if (onCreated) onCreated(data);
        } catch (err: any) {
            if (apiErrorCode(err) === "CaptchaFailed") {
                // The failed attempt consumed the token (single-use): remount
                // the gate widget for a fresh one, and let a known-email
                // gate auto-save again once it arrives.
                setCaptchaToken(null);
                setCaptchaNonce((n) => n + 1);
                autoCreateTriedRef.current = false;
            }
            setSaveState("error");
            setSaveError(saveErrorMessage(err, true));
        } finally {
            savingRef.current = false;
        }
        // Catch up on any package change made while the create was in flight.
        if (createdRef.current) flushEdits();
    };

    // Authenticated viewers save on arrival at step 3; a known-email lead
    // saves as soon as the gate's captcha solves. Tried once — failures
    // surface a Retry rather than looping.
    const autoCreate =
        !editing &&
        !created &&
        strategyReady &&
        (!needsEmail || (emailKnown && (!captchaRequired || !!captchaToken)));
    useEffect(() => {
        if (!autoCreate || autoCreateTriedRef.current) return;
        autoCreateTriedRef.current = true;
        createStrategy();
    }, [autoCreate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Every later change is an edit: debounce so a burst of clicks becomes
    // one PATCH.
    useEffect(() => {
        if (editing || !created || !strategyReady) return undefined;
        if (editSig === savedSigRef.current) return undefined;
        const id = setTimeout(flushEdits, 600);
        return () => clearTimeout(id);
    }, [editing, created, strategyReady, editSig]); // eslint-disable-line react-hooks/exhaustive-deps

    // A changed strategy can be emailed again.
    useEffect(() => {
        setEmailedPdf(false);
    }, [editSig]);

    const retrySave = () => {
        if (createdRef.current) {
            flushEdits();
        } else {
            autoCreateTriedRef.current = true;
            createStrategy();
        }
    };

    // Edit mode: one explicit save of the whole selection.
    const submit = async () => {
        if (!canSubmit || !edit || !objectiveId || !categoryId || !tier) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            const { data } = await axiosInstant.patch<ProposalDetail>(edit.endpoint, editBody);
            if (onSaved) onSaved(data);
        } catch (err: any) {
            const message = saveErrorMessage(err, false);
            setSubmitError(
                apiErrorCode(err) === "ProposalNotEditable" || apiErrorCode(err) === "rate_limit_exceeded"
                    ? message
                    : "Something went wrong saving your changes. Please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    // Post-create actions reuse the in-memory wizard state for the PDF.
    const pdfInput = () => ({
        strategy: strategyFromWizard({
            objective,
            secondaryObjective,
            categoryName,
            selectedChannels,
            tier: tier || "better",
            preview,
        }),
        proposal: {
            id: created ? created.id : null,
            name: created ? created.name : null,
            brandName:
                (brands && brands.length > 0 && brands[0].name) ||
                (product && product.brand_name) ||
                null,
            contactEmail:
                email.trim() ||
                (user && (user.emailAddress || user.email_address)
                    ? String(user.emailAddress || user.email_address)
                    : null),
            createdAt: new Date().toISOString(),
            shareUrl:
                created && created.share_token
                    ? `${window.location.origin}/proposal/view/${created.share_token}`
                    : null,
            pricingTotal,
            // When launched from an audit CTA, the audited URL is the
            // product URL that rode along in the query params.
            auditUrl: pdpAuditReportId && product && product.url ? product.url : null,
            auditDate: null,
        },
        tiers: preview ? preview.tiers : null,
        unitsByTier: {
            good: computeUnits(objective, secondaryObjective, "good"),
            better: computeUnits(objective, secondaryObjective, "better"),
            best: computeUnits(objective, secondaryObjective, "best"),
        },
        examples,
        internal: internalViewer,
        showPricing: authedViewer && !pricingHidden,
        showPriceTeaser: !pricingHidden,
        // RMN-flavored copy only when the config is actually lensed (the
        // server sends `rmn` for rmn:true roots only) — non-RMN brands go
        // through the /rmn endpoints too but get the standard copy.
        rmn: !!(config && config.rmn),
    });

    const emailMyProposal = async () => {
        if (!created || !created.share_token || emailingPdf) return;
        setEmailingPdf(true);
        setEmailPdfError(null);
        try {
            const pdfBase64 = await proposalPdfBase64(pdfInput());
            // Server rejects attachments over 8MB of base64 (413) — skip the
            // doomed upload and point at Download instead.
            if (pdfBase64.length > MAX_EMAIL_PDF_BASE64_CHARS) {
                setEmailPdfError(`This ${noun} is too large to email — please use the download button instead.`);
                return;
            }
            await axiosInstant.post(publicProposalEmailEndpoint(created.share_token), {
                pdfBase64,
                filename: "video-strategy.pdf",
            });
            setEmailedPdf(true);
        } catch (err: any) {
            const code = apiErrorCode(err);
            if (code === "PdfTooLarge") {
                setEmailPdfError(`This ${noun} is too large to email — please use the download button instead.`);
            } else if (code === "NoRecipientOnFile") {
                setEmailPdfError(`We don't have an email address on file for this ${noun}.`);
            } else {
                setEmailPdfError("We couldn't send the email. Please try again, or download the PDF.");
            }
        } finally {
            setEmailingPdf(false);
        }
    };

    if (loading) {
        return (
            <div style={{ fontFamily: FONT, color: TEXT_MUTED, fontSize: 14, padding: "40px 0", textAlign: "center" }}>
                Loading the {noun} builder…
            </div>
        );
    }
    if (configError || !config) {
        return (
            <div style={{ fontFamily: FONT, color: TEXT_MUTED, fontSize: 14, padding: "40px 0", textAlign: "center" }}>
                {configError || `We couldn't load the ${noun} builder. Please try again.`}
            </div>
        );
    }

    const shareUrl =
        created && created.share_token
            ? `${window.location.origin}/proposal/view/${created.share_token}`
            : null;
    // Email-me is a public-page convenience; RMN and internal users work
    // inside the portal, so both are hidden.
    const showEmailMe = !!shareUrl && !rmn && !internalViewer;
    // Anonymous leads: register with the strategy + product in tow
    // (TDC-1573). The token proves ownership server-side; the product URL
    // becomes the brand's first scored product.
    const showCreateAccount = !!shareUrl && !rmn && !internalViewer && !user;
    // The analysis this strategy was suggested from rides along by its
    // access token, so it lands on the product too.
    const intelHandoff =
        intelAccessToken && (consumerVoiceAuditId || aiPresenceAuditId)
            ? { id: (consumerVoiceAuditId || aiPresenceAuditId) as number, token: intelAccessToken }
            : null;
    const registerHref =
        showCreateAccount && created
            ? buildRegisterHandoffPath({
                  email: (emailProp || email || "").trim(),
                  url: product && product.url ? product.url : undefined,
                  auditReportId: pdpAuditReportId || undefined,
                  strategyToken: created.share_token || undefined,
                  consumerVoice: consumerVoiceAuditId ? intelHandoff : null,
                  aiPresence: !consumerVoiceAuditId && aiPresenceAuditId ? intelHandoff : null
              })
            : "";

    const copyShareLink = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopiedLink(true);
            setTimeout(() => setCopiedLink(false), 1800);
        } catch {
            // Clipboard unavailable — the view link in the saved panel still works.
        }
    };

    const barButton: React.CSSProperties = { ...buttonBase, padding: "10px 16px" };
    const outlineButton = (active: boolean): React.CSSProperties => ({
        ...barButton,
        background: "transparent",
        color: active ? GREEN : GOLD_DARK,
        border: `1px solid ${active ? GREEN : GOLD_DARK}`,
    });

    let emailMeLabel = "Email me";
    if (emailingPdf) emailMeLabel = "Sending…";
    if (emailedPdf) emailMeLabel = "✓ Sent to your inbox";

    let saveStatus: React.ReactNode = null;
    if (saveState === "saving") {
        saveStatus = <span style={{ fontSize: 12.5, color: TEXT_FAINT }}>Saving…</span>;
    } else if (saveState === "error") {
        saveStatus = (
            <span style={{ fontSize: 12.5, color: RED }}>
                {saveError}{" "}
                {!lockedRef.current && (
                    <button
                        type="button"
                        onClick={retrySave}
                        style={{ background: "none", border: "none", padding: 0, color: RED, fontWeight: 700, textDecoration: "underline", cursor: "pointer", fontFamily: FONT, fontSize: 12.5 }}
                    >
                        Retry
                    </button>
                )}
            </span>
        );
    } else if (saveState === "saved") {
        saveStatus = <span style={{ fontSize: 12.5, color: GREEN, fontWeight: 600 }}>✓ Saved</span>;
    }

    return (
        <div style={{ fontFamily: FONT }}>
            <WizardStepper step={step} onStepClick={(s) => setStep(s)} />

            {step === 1 && (pdpAuditReportId || pdpUrlScoreId || consumerVoiceAuditId || aiPresenceAuditId) && (suggestionApplied || suggestionLoading) && (
                <div
                    style={{
                        fontFamily: FONT,
                        display: "flex",
                        gap: 8,
                        alignItems: "flex-start",
                        fontSize: 13,
                        color: TEXT_MUTED,
                        background: GOLD_TINT,
                        border: `1px solid ${BORDER}`,
                        borderRadius: 10,
                        padding: "10px 14px",
                        marginBottom: 16,
                        lineHeight: 1.55,
                    }}
                >
                    {suggestionApplied ? (
                        <span style={{ fontSize: 15 }}>✨</span>
                    ) : (
                        <span
                            style={{
                                width: 13,
                                height: 13,
                                marginTop: 3,
                                border: `2px solid ${BORDER}`,
                                borderTopColor: GOLD_DARK,
                                borderRadius: "50%",
                                animation: "tdcSuggestSpin 0.8s linear infinite",
                                display: "inline-block",
                                flexShrink: 0,
                            }}
                        />
                    )}
                    <span>
                        {suggestionApplied ? (
                            <>
                                <strong>Suggested from your {suggestionSource.name}</strong> — we pre-selected the
                                category and goals below based on {suggestionSource.basis}.
                                {suggestion && suggestion.rationale ? ` ${suggestion.rationale}` : ""}{" "}
                                Feel free to change anything.
                            </>
                        ) : (
                            `Reading your ${suggestionSource.name} to suggest a category and goals…`
                        )}
                    </span>
                    <style>{`@keyframes tdcSuggestSpin { to { transform: rotate(360deg); } }`}</style>
                </div>
            )}
            {step === 1 && (
                <Step1Objective
                    objectives={config.objectives}
                    categories={config.product_categories}
                    categoryId={categoryId}
                    objectiveId={objectiveId}
                    secondaryObjectiveId={secondaryObjectiveId}
                    onCategoryChange={(id) => {
                        userPickedRef.current = true;
                        setCategoryId(id);
                    }}
                    onObjectiveChange={(id) => {
                        userPickedRef.current = true;
                        setObjectiveId(id);
                    }}
                    onSecondaryObjectiveChange={(id) => {
                        userPickedRef.current = true;
                        setSecondaryObjectiveId(id);
                    }}
                    rmnCategories={rmn ? config.rmn_categories : undefined}
                    rmnCategoryId={rmnCategoryId}
                    onRmnCategoryChange={handleRmnCategoryChange}
                    rmnName={config.rmn ? config.rmn.name : null}
                    showProductPicker={!!rmn && !editing && !created}
                    products={selectedProducts}
                    onProductsChange={setSelectedProducts}
                />
            )}
            {step === 2 && (
                <Step2Channels
                    channels={config.channels}
                    objective={objective}
                    selectedChannels={selectedChannels}
                    onChange={setSelectedChannels}
                    pdpSubtype={pdpSubtype}
                    onPdpSubtypeChange={setPdpSubtype}
                    rmn={!!config.rmn}
                />
            )}
            {step === 3 && (
                <>
                    {/* Sticky action bar — keeps the share actions in view
                        while the long strategy layout scrolls. Create mode
                        has no submit: the strategy saves at reveal and every
                        change autosaves, so the bar shows the save status
                        and the download / email / link / account actions
                        once the first save lands. Edit mode keeps an
                        explicit Save (Cancel must still discard). */}
                    <div
                        style={{
                            position: "sticky",
                            top: 12,
                            zIndex: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 12,
                            background: CARD_BG,
                            border: `1px solid ${BORDER}`,
                            borderRadius: 12,
                            padding: "10px 14px",
                            marginBottom: 18,
                            boxShadow: "0 6px 18px rgba(28,26,23,0.08)",
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => setStep(2)}
                            style={{
                                ...barButton,
                                background: "transparent",
                                color: TEXT_MUTED,
                                border: `1px solid ${BORDER}`,
                            }}
                        >
                            ← Back
                        </button>
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                            {editing && (
                                <>
                                    {onCancelEdit && (
                                        <button
                                            type="button"
                                            onClick={onCancelEdit}
                                            style={{
                                                ...barButton,
                                                background: "transparent",
                                                color: TEXT_MUTED,
                                                border: `1px solid ${BORDER}`,
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        disabled={!canSubmit}
                                        onClick={submit}
                                        style={{
                                            ...buttonBase,
                                            background: canSubmit ? GOLD_DARK : DISABLED_BG,
                                            color: "#fff",
                                        }}
                                    >
                                        {submitting ? "Saving…" : "Save changes"}
                                    </button>
                                </>
                            )}
                            {!editing && created && (
                                <>
                                    {saveStatus}
                                    <button
                                        type="button"
                                        onClick={() => downloadProposalPdf(pdfInput())}
                                        style={{ ...barButton, background: INK_BG, color: INK_TEXT }}
                                    >
                                        Download PDF ↓
                                    </button>
                                    {showEmailMe && (
                                        <button
                                            type="button"
                                            disabled={emailingPdf || emailedPdf}
                                            onClick={emailMyProposal}
                                            style={outlineButton(emailedPdf)}
                                        >
                                            {emailMeLabel}
                                        </button>
                                    )}
                                    {shareUrl && (
                                        <button type="button" onClick={copyShareLink} style={outlineButton(copiedLink)}>
                                            {copiedLink ? "✓ Link copied" : "⧉ Share link"}
                                        </button>
                                    )}
                                    {showCreateAccount && (
                                        <a
                                            href={registerHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                ...barButton,
                                                display: "inline-block",
                                                background: GOLD_DARK,
                                                color: "#fff",
                                                textDecoration: "none",
                                            }}
                                        >
                                            Create free account →
                                        </a>
                                    )}
                                </>
                            )}
                            {!editing && !created && (
                                <>
                                    {!emailGateActive && saveStatus}
                                    <span style={{ fontFamily: FONT, fontSize: 11.5, color: TEXT_FAINT }}>
                                        There is no obligation at this stage.
                                    </span>
                                </>
                            )}
                        </div>
                    </div>

                    {emailPdfError && (
                        <div style={{ fontFamily: FONT, fontSize: 13, color: RED, margin: "-8px 0 14px", textAlign: "right" }}>
                            {emailPdfError}
                        </div>
                    )}

                    {/* What the old "finished" screen said, now alongside
                        the live strategy. */}
                    {created && !editing && (
                        <div
                            style={{
                                fontFamily: FONT,
                                display: "flex",
                                gap: 12,
                                alignItems: "flex-start",
                                background: GREEN_BG,
                                border: `1px solid ${BORDER}`,
                                borderRadius: 12,
                                padding: "14px 16px",
                                marginBottom: 18,
                            }}
                        >
                            <span
                                style={{
                                    width: 26,
                                    height: 26,
                                    flexShrink: 0,
                                    borderRadius: "50%",
                                    background: CARD_BG,
                                    color: GREEN,
                                    fontSize: 14,
                                    fontWeight: 700,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                ✓
                            </span>
                            <div style={{ fontSize: 13.5, color: TEXT_MUTED, lineHeight: 1.6 }}>
                                <strong style={{ color: BLACK }}>Your {noun} is saved.</strong>{" "}
                                We've saved your custom video strategy
                                {pricingTotal != null && !pricingHidden ? " and estimate" : ""}. Our team will
                                review it and follow up within 1 business day. Pick a different package or go
                                back to adjust anything — changes save automatically. There is no obligation at
                                this stage.
                                {showCreateAccount && (
                                    <>
                                        {" "}
                                        <strong style={{ color: BLACK }}>Create a free account</strong> and your
                                        strategy and product come with you — we'll add this product to your catalog
                                        and score it weekly for free.
                                    </>
                                )}
                                {shareUrl && (
                                    <>
                                        {" "}
                                        Revisit or edit any time:{" "}
                                        <a href={`/proposal/view/${created.share_token}`} style={{ color: GOLD_DARK, fontWeight: 600 }}>
                                            view your {noun}
                                        </a>
                                        .
                                    </>
                                )}
                                {showCreateAccount && (
                                    <div style={{ marginTop: 12 }}>
                                        <a
                                            href={registerHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                display: "inline-block",
                                                background: GOLD_DARK,
                                                color: "#fff",
                                                fontSize: 15,
                                                fontWeight: 700,
                                                borderRadius: 8,
                                                padding: "12px 22px",
                                                textDecoration: "none"
                                            }}
                                        >
                                            Create your free account →
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* The strategy always renders; while the email gate is
                        up it sits blurred and inert under the capture
                        overlay. No leak risk: anonymous payloads carry no
                        pricing, so the blur is presentation, not security. */}
                    <div style={{ position: "relative" }}>
                        <div
                            aria-hidden={emailGateActive || undefined}
                            style={
                                emailGateActive
                                    ? {
                                        filter: "blur(6px)",
                                        pointerEvents: "none",
                                        userSelect: "none",
                                    }
                                    : undefined
                            }
                        >
                            <Step3Packages
                                objective={objective}
                                secondaryObjective={secondaryObjective}
                                categoryName={categoryName}
                                selectedChannels={selectedChannels}
                                tier={tier}
                                onTierChange={setTier}
                                preview={preview}
                                previewLoading={previewLoading}
                                showBreakdowns={internalViewer}
                                showPricing={authedViewer && !pricingHidden}
                                showPriceTeaser={!pricingHidden}
                                examples={examples}
                                description={edit ? edit.proposal.description : suggestion && suggestion.description}
                            />
                        </div>
                        {emailGateActive && (
                            <div
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 20,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    background: "rgba(250,250,248,0.35)",
                                    borderRadius: 14,
                                }}
                            >
                                <div
                                    style={{
                                        position: "sticky",
                                        top: 90,
                                        margin: "56px 16px 24px",
                                        width: "100%",
                                        maxWidth: 440,
                                        boxSizing: "border-box",
                                        background: CARD_BG,
                                        border: `1px solid ${BORDER}`,
                                        borderRadius: 14,
                                        padding: "30px 26px",
                                        textAlign: "center",
                                        boxShadow: "0 14px 36px rgba(28,26,23,0.14)",
                                    }}
                                >
                                    <h2
                                        style={{
                                            fontFamily: FONT,
                                            fontSize: 21,
                                            fontWeight: 700,
                                            color: BLACK,
                                            margin: "0 0 8px",
                                        }}
                                    >
                                        Your video strategies are ready
                                    </h2>
                                    <p
                                        style={{
                                            fontFamily: FONT,
                                            fontSize: 14,
                                            color: TEXT_MUTED,
                                            lineHeight: 1.6,
                                            margin: "0 0 18px",
                                        }}
                                    >
                                        {emailKnown
                                            ? "Confirm you're human to reveal and save your Good / Better / Best packages."
                                            : "Enter your work email to reveal your Good / Better / Best packages — we'll save your strategy so you can share it or come back any time."}
                                    </p>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && gateReady) {
                                                createStrategy();
                                            }
                                        }}
                                        placeholder="you@company.com"
                                        style={{
                                            fontFamily: FONT,
                                            fontSize: 14,
                                            color: BLACK,
                                            width: "100%",
                                            padding: "12px 14px",
                                            borderRadius: 10,
                                            border: `1px solid ${BORDER}`,
                                            background: CARD_BG,
                                            boxSizing: "border-box",
                                            marginBottom: 12,
                                        }}
                                    />
                                    {captchaRequired && !editing && (
                                        <div style={{ width: "100%", marginBottom: 12 }}>
                                            <TurnstileWidget
                                                key={captchaNonce}
                                                onToken={setCaptchaToken}
                                                theme="light"
                                                size="flexible"
                                            />
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        disabled={!gateReady}
                                        onClick={createStrategy}
                                        style={{
                                            ...buttonBase,
                                            width: "100%",
                                            background: gateReady ? GOLD_DARK : DISABLED_BG,
                                            color: "#fff",
                                        }}
                                    >
                                        {saveState === "saving" ? "Saving…" : "Reveal my strategies →"}
                                    </button>
                                    {saveState === "error" && saveError && (
                                        <p style={{ fontFamily: FONT, fontSize: 12.5, color: RED, margin: "12px 0 0" }}>
                                            {saveError}
                                        </p>
                                    )}
                                    <p
                                        style={{
                                            fontFamily: FONT,
                                            fontSize: 11.5,
                                            color: TEXT_FAINT,
                                            margin: "12px 0 0",
                                        }}
                                    >
                                        There is no obligation at this stage.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {submitError && (
                        <div
                            style={{
                                fontFamily: FONT,
                                marginTop: 16,
                                fontSize: 13,
                                color: RED,
                                background: RED_BG,
                                border: `1px solid ${RED}`,
                                borderRadius: 10,
                                padding: "10px 14px",
                            }}
                        >
                            {submitError}
                        </div>
                    )}
                </>
            )}

            {/* Steps 1–2 keep the bottom nav; step 3's Back and actions all
                live in the sticky bar above. */}
            {step < 3 && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 28,
                        gap: 12,
                    }}
                >
                    {step > 1 ? (
                        <button
                            type="button"
                            onClick={() => setStep((s) => (s - 1) as Step)}
                            style={{
                                ...buttonBase,
                                background: "transparent",
                                color: TEXT_MUTED,
                                border: `1px solid ${BORDER}`,
                            }}
                        >
                            ← Back
                        </button>
                    ) : editing && onCancelEdit ? (
                        <button
                            type="button"
                            onClick={onCancelEdit}
                            style={{
                                ...buttonBase,
                                background: "transparent",
                                color: TEXT_MUTED,
                                border: `1px solid ${BORDER}`,
                            }}
                        >
                            Cancel
                        </button>
                    ) : (
                        <span />
                    )}

                    <button
                        type="button"
                        disabled={step === 1 ? !canContinueStep1 : !canContinueStep2}
                        onClick={() => setStep((s) => (s + 1) as Step)}
                        style={{
                            ...buttonBase,
                            background: (step === 1 ? canContinueStep1 : canContinueStep2) ? INK_BG : DISABLED_BG,
                            color: INK_TEXT,
                        }}
                    >
                        {step === 2 ? "See my strategy →" : "Continue →"}
                    </button>
                </div>
            )}
        </div>
    );
}
